import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { isPhone, useDesktopPreference } from '../mobile/device';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, BookOpen, ClipboardList, Clapperboard, FolderHeart, Inbox, Library, LogOut, Settings2, Shapes, Workflow, WifiOff } from 'lucide-react';
import { GraphicsEditorHost } from '../graphics/GraphicsEditor';
import { api } from '../api/client';
import { useAlertSummary, useIntakeSummary, useTaskSummary, type Me } from '../api/hooks';
import { useLiveEvents } from '../api/events';
import { uploader } from '../upload/uploader';
import { UploadTray } from './UploadTray';
import { UpdateNotice } from './UpdateNotice';
import { VersionBadge } from './VersionHistory';
import { AccountDialog, ssoLinkReturned } from './ChangePassword';
import { DropZone, pickFiles } from './DropZone';
import { ShortcutsHelp } from './Shortcuts';
import { Toasts, toast } from './Toasts';
import { isTyping } from '../lib/keys';
import { plainClick, ViewTransitionSettle, withViewTransition } from '../lib/viewTransition';
import { queryKeys } from '../api/queryKeys';

/** `short` is the text shown under the icon when the full label is too wide for the rail. */
function RailLink({ to, icon, label, short, badge }: { to: string; icon: ReactNode; label: string; short?: string; badge?: ReactNode }) {
  const navigate = useNavigate();
  // A page transition (1.2.6): the active item's pill slides to the new page (view-transition-name rail-active).
  return (
    <NavLink to={to} className={({ isActive }) => `rail-item ${isActive ? 'active' : ''}`} aria-label={label} title={label}
      onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); withViewTransition(() => navigate(to)); }}>
      {({ isActive }) => (
        <>
          {isActive && <span className="rail-pill" aria-hidden />}
          {icon}{badge}
          <span className="rail-label">{short ?? label}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * Tasks, with how many need you: open tasks, approvals you can decide, and new decisions or comments on what
 * you asked to be approved (0.31.0). Red when a task is overdue.
 */
function TasksLink() {
  const s = useTaskSummary().data;
  const n = (s?.open ?? 0) + (s?.approvals ?? 0) + (s?.unseen ?? 0);
  const title = s ? [`${s.open} open`, s.overdue ? `${s.overdue} overdue` : '', s.approvals ? `${s.approvals} to approve` : '', s.unseen ? `${s.unseen} new on your approvals` : ''].filter(Boolean).join(', ') : '';
  return (
    <RailLink to={s && !s.open && (s.approvals || s.unseen) ? '/tasks?view=approvals' : '/tasks'} icon={<ClipboardList size={19} />} label="Tasks"
      badge={n ? <span className={`rail-badge ${s?.overdue ? 'overdue' : ''}`} data-testid="tasks-badge" title={title}>{n}</span> : null} />
  );
}

/**
 * Admin, with how many System status alerts show now (1.2.8): errors and warnings, suppressed ones left out, as the
 * Overview lists them. Red when any is an error, else yellow. Administrators only; with alerts it opens System status.
 */
function AdminLink({ me }: { me: Me }) {
  const s = useAlertSummary(me.permissions.includes('admin')).data;
  const n = (s?.errors ?? 0) + (s?.warnings ?? 0);
  const title = s ? [s.errors ? `${s.errors} error${s.errors === 1 ? '' : 's'}` : '', s.warnings ? `${s.warnings} warning${s.warnings === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ') : '';
  return (
    <RailLink to={n ? '/admin/status' : '/admin'} icon={<Settings2 size={19} />} label="Admin"
      badge={n ? <span className={`rail-badge ${s?.errors ? 'error' : 'warning'}`} data-testid="admin-badge" title={title}>{n}</span> : null} />
  );
}

/**
 * Intake (1.3.0), with how many media portal uploads wait for review; red when any is overdue. Shown only with
 * portal.review. The count comes from useIntakeSummary (api/hooks/intake.ts, stream P4).
 */
function IntakeLink() {
  const s = useIntakeSummary(true).data;
  const n = s?.waiting ?? 0;
  const title = s ? [`${s.waiting} waiting`, s.overdue ? `${s.overdue} overdue` : ''].filter(Boolean).join(', ') : '';
  return (
    <RailLink to="/intake" icon={<Inbox size={19} />} label="Intake"
      badge={n ? <span className={`rail-badge ${s?.overdue ? 'overdue' : ''}`} data-testid="intake-badge" title={title}>{n}</span> : null} />
  );
}

export function Shell({ me }: { me: Me }) {
  const [help, setHelp] = useState(false);
  const [, setDesktop] = useDesktopPreference();
  const phone = isPhone();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const live = useLiveEvents(true);
  const [online, setOnline] = useState(navigator.onLine);
  const canUpload = me.permissions.includes('asset.upload');

  useEffect(() => {
    uploader.onAssetCreated = (assetId) => { void qc.invalidateQueries({ queryKey: queryKeys.assets() }); void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) }); };
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') { e.preventDefault(); setHelp(true); }
      if (e.key.toLowerCase() === 'u' && canUpload) { e.preventDefault(); pickFiles(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.removeEventListener('keydown', onKey);
    };
  }, [qc, canUpload]);

  async function logout() {
    await api('/api/v1/auth/logout', { method: 'POST', json: {} }).catch(() => undefined);
    qc.clear();
    navigate('/login');
    toast('Signed out');
  }

  // Back from Link single sign-on (1.0.0-rc.14): My account opens and says how it went.
  const [account, setAccount] = useState(ssoLinkReturned);
  const initials = me.user.displayName.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="shell">
      <ViewTransitionSettle />
      <nav className="rail" aria-label="Workspaces">
        <Link to="/" aria-label="PRISM home" title="Home" className="rail-logo-link"
          onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); withViewTransition(() => navigate('/')); }}><img src="/prism.svg" alt="PRISM" className="rail-logo" /></Link>
        <RailLink to="/library" icon={<Library size={19} />} label="Library" />
        <RailLink to="/collections" icon={<FolderHeart size={19} />} label="Collections" short="Collect" />
        <RailLink to="/create" icon={<Clapperboard size={19} />} label="Create" />
        <RailLink to="/graphics" icon={<Shapes size={19} />} label="Graphics" />
        <RailLink to="/workflows" icon={<Workflow size={19} />} label="Flows" />
        <RailLink to="/monitor" icon={<Activity size={19} />} label="Monitor" />
        {me.permissions.includes('portal.review') && <IntakeLink />}
        <TasksLink />
        <AdminLink me={me} />
        <RailLink to="/help" icon={<BookOpen size={19} />} label="Help" />
        <div className="rail-spacer" />
        <button className="rail-item" onClick={() => void logout()} aria-label="Sign out" title="Sign out" style={{ border: 0, background: 'transparent', cursor: 'pointer' }}>
          <LogOut size={17} />
        </button>
        <VersionBadge />
        <button className="rail-avatar" title={`${me.user.displayName} · ${me.roleLabel} · ${me.workspace.name} · My account`} onClick={() => setAccount(true)} data-testid="current-user" style={{ border: 0, cursor: 'pointer' }}>{initials}</button>
      </nav>
      <div className="main">
        {phone && <div className="phone-banner"><Link to="/m" onClick={() => setDesktop(false)} data-testid="to-mobile">Switch to the phone version</Link></div>}
        {(!online || live === 'offline') && (
          <div className="offline-bar" role="status">
            <WifiOff size={14} /> {online ? 'Live updates are disconnected. Reconnecting…' : 'You are offline. Changes will not be saved until the connection returns.'}
          </div>
        )}
        <UpdateNotice live={live} />
        <Outlet />
      </div>
      <DropZone enabled={canUpload} />
      <UploadTray />
      <GraphicsEditorHost />
      <Toasts />
      {help && <ShortcutsHelp onClose={() => setHelp(false)} />}
      {account && <AccountDialog me={me} onClose={() => setAccount(false)} />}
    </div>
  );
}

